import { useState, type MouseEvent, type ReactNode } from "react";
import { ClipboardList, Copy, FileText, Heading, Link2 } from "lucide-react";
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuSeparator,
	ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { toMarkdown } from "@/components/projects/project-description-utils";
import { copyToClipboard } from "./utils";

const TEXT_SELECTOR =
	"h1,h2,h3,h4,h5,h6,p,li,pre,blockquote,td,th,label,a,button,span";
const EDITABLE_SELECTOR = "input,textarea,select,[contenteditable='true']";

// Text under the cursor: active selection first, else the nearest text element.
function resolveText(e: MouseEvent<HTMLElement>): string {
	const selection = window.getSelection()?.toString().trim();
	if (selection) return selection;
	const target = e.target as HTMLElement;
	const el =
		target.closest<HTMLElement>(TEXT_SELECTOR) ??
		(target !== e.currentTarget ? target : null);
	return el?.innerText.trim() ?? "";
}

interface Props {
	title: string;
	description?: string | null;
	link?: string;
	className?: string;
	children: ReactNode;
}

export function CopyTextContextMenu({
	title,
	description,
	link,
	className,
	children,
}: Props) {
	const [text, setText] = useState("");
	const descriptionMd = toMarkdown(description).trim();

	function handleContextMenuCapture(e: MouseEvent<HTMLElement>) {
		// Keep the native menu (paste, spellcheck) inside editors and inputs.
		if ((e.target as HTMLElement).closest(EDITABLE_SELECTOR)) {
			e.stopPropagation();
			return;
		}
		setText(resolveText(e));
	}

	return (
		<ContextMenu>
			<ContextMenuTrigger
				className={className}
				onContextMenuCapture={handleContextMenuCapture}
			>
				{children}
			</ContextMenuTrigger>
			<ContextMenuContent>
				{text && (
					<>
						<ContextMenuItem onClick={() => copyToClipboard(text, "Text")}>
							<Copy />
							<span className="max-w-56 truncate">
								Copy "{text}"
							</span>
						</ContextMenuItem>
						<ContextMenuSeparator />
					</>
				)}
				<ContextMenuItem onClick={() => copyToClipboard(title, "Title")}>
					<Heading />
					Copy title
				</ContextMenuItem>
				{descriptionMd && (
					<ContextMenuItem
						onClick={() => copyToClipboard(descriptionMd, "Description")}
					>
						<FileText />
						Copy description
					</ContextMenuItem>
				)}
				{descriptionMd && (
					<ContextMenuItem
						onClick={() =>
							copyToClipboard(
								`${title}\n\n${descriptionMd}`,
								"Title and description",
							)
						}
					>
						<ClipboardList />
						Copy title + description
					</ContextMenuItem>
				)}
				{link && (
					<ContextMenuItem onClick={() => copyToClipboard(link, "Link")}>
						<Link2 />
						Copy task link
					</ContextMenuItem>
				)}
			</ContextMenuContent>
		</ContextMenu>
	);
}
